// ManageBooking.jsx
import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import usePost from "../hooks/usePost";
import { useTranslation } from "react-i18next";
import Footer from "../components/Footer";
import Navbar2 from "../components/Navbar2";

const InputField = React.memo(({ label, icon, type = "text", value, onChange, onEnter, placeholder, uppercase }) => {
  const { t } = useTranslation();

  return (
    <div>
      <label className="block mb-2 text-sm font-semibold text-slate-700">{label}</label>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none sm:pl-4">
          <i className={`fa-solid ${icon} text-slate-400`} />
        </div>
        <input
          type={type}
          value={value}
          onChange={onChange}
          onKeyDown={(e) => e.key === "Enter" && onEnter()}
          className={`w-full py-3 pl-10 pr-4 transition-all border rounded-sm border-slate-300 outline-none 
            focus:ring-2 focus:ring-red-500 focus:border-transparent sm:pl-12 ${uppercase ? "uppercase" : ""}`}
          placeholder={placeholder}
        />
      </div>
    </div>
  );
});

function ManageBooking() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ email: "", bookingCode: "" });
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const login = usePost({ module: "bookings/login" });

  const handleChange = useCallback(
    (field) => (e) => {
      const value = field === "bookingCode" ? e.target.value.toUpperCase() : e.target.value;
      setForm((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleCheckBooking = useCallback(async () => {
    setError("");
    const { email, bookingCode } = form;

    if (!email || !bookingCode) {
      setError(t("validation.fill_all_fields"));
      return;
    }

    try {
      const response = await login.mutateAsync({ email, booking_code: bookingCode });
      navigate(`/booking/${response.data.data.id}`);
    } catch (err) {
      setError(err.message || t("common.error_occurred"));
    }
  }, [form, login, navigate, t]);

  const isLoading = login.isPending;
  const { email, bookingCode } = form;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <>
      <Navbar2 darkBackground />
      <div className="min-h-screen px-4 py-8 mt-32 sm:py-12">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <header className="mb-8 text-center sm:mb-12">
            <h1 className="mb-3 text-2xl font-bold text-slate-900 sm:text-4xl">{t("booking.check_history")}</h1>
            <p className="text-base text-slate-600 sm:text-lg">{t("booking.enter_details")}</p>
          </header>

          {/* Form */}
          <div className="p-6 space-y-4 bg-white border rounded-lg shadow-xl border-slate-200 sm:p-8 sm:space-y-6">
            <InputField
              label={t("common.email")}
              icon="fa-envelope"
              type="email"
              value={email}
              onChange={handleChange("email")}
              onEnter={handleCheckBooking}
              placeholder="email@example.com"
            />

            <InputField
              // label={t("booking.booking_code")}
              label={"Booking Number"}
              icon="fa-ticket"
              value={bookingCode}
              onChange={handleChange("bookingCode")}
              onEnter={handleCheckBooking}
              placeholder="S-0018"
              uppercase
            />

            {/* <p className="text-xs text-slate-500 sm:text-sm">{t("booking.code_example")}</p> */}

            {error && (
              <div className="flex items-start gap-3 p-4 border border-red-200 rounded-sm bg-red-50">
                <i className="fa-solid fa-circle-exclamation text-red-600 mt-0.5" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <button
              onClick={handleCheckBooking}
              disabled={isLoading || !email || !bookingCode}
              className="w-full bg-[#f5272b] hover:bg-red-700 text-white font-bold py-3 rounded-sm shadow-lg hover:shadow-xl 
              transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 sm:py-4"
            >
              {isLoading ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin" />
                  <span>{t("common.searching")}</span>
                </>
              ) : (
                <>
                  <i className="fa-solid fa-search" />
                  <span>{t("booking.check_booking")}</span>
                </>
              )}
            </button>
          </div>

          {/* Info */}
          <div className="p-4 mt-6 border border-blue-200 rounded-lg bg-blue-50 sm:p-6 sm:mt-8">
            <div className="flex gap-3">
              <i className="mt-1 text-blue-600 fa-solid fa-circle-info" />
              <div className="space-y-2">
                <p className="font-semibold text-blue-900">{t("common.information")}:</p>
                <ul className="space-y-1 text-xs text-blue-800 list-disc list-inside sm:text-sm">
                  <li>{t("booking.info_code_sent")}</li>
                  <li>{t("booking.info_continue_payment")}</li>
                  <li>{t("booking.info_contact_support")}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default React.memo(ManageBooking);
